import React, {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
    Box,
    Button,
    Collapse,
    Divider,
    Grid,
    Paper,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import {useMutation, useQuery} from "@apollo/client";
import {withAuthenticationRequired} from "@auth0/auth0-react";
import {useTranslation} from "react-i18next";

import Loading from "../components/common/Loading";
import AppSnackbar from "../common/AppSnackbar";
import AlertDialogSlide from "../components/common/AlertDialogSlide";
import {useContractAPI} from "../service/useContractAPI";

import {CONTRACT_TEMPLATES, UPSERT_CONTRACT_TEMPLATE} from "../gql/contractTemplates.gql";
import {CONTRACT_COMMON_PARAMS, CONTRACT_RESERVATION_PARAMS, DEFAULT_CONTRACT_CSS, DEFAULT_PREVIEW_VALUES, editorPdfLikeSx} from "./editor/constants";
import {useEditor} from "@tiptap/react";
import {StarterKit} from "@tiptap/starter-kit";
import {Link} from "@tiptap/extension-link";
import {Image} from "@tiptap/extension-image";

import {
    RichTextEditorProvider,
    RichTextField,
    MenuControlsContainer,
    MenuDivider,
    MenuSelectHeading,
    MenuButtonBold,
    MenuButtonItalic,
    MenuButtonBulletedList,
    MenuButtonUnderline,
    MenuButtonOrderedList,
    MenuButtonUndo,
    MenuButtonRedo,
    MenuButtonTextColor,
} from "mui-tiptap";

import {renderTemplate, exportToTemplateString, migrateLegacyPlaceholders,} from "./editor/utils/contractTemplateUtils";
import {PageBreak} from "./editor/extensions/PageBreak";
import {Placeholder} from "./editor/extensions/Placeholder";
import {MenuButtonInsertPlaceholder} from "./editor/components/MenuButtonInsertPlaceholder";
import {MenuButtonPageBreak} from "./editor/components/MenuButtonPageBreak";
import {TextStyle} from "@tiptap/extension-text-style";
import {Color} from "@tiptap/extension-color";

const ContractTemplateEditor = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [previewValues, setPreviewValues] = useState(DEFAULT_PREVIEW_VALUES);
    const [showPreviewParams, setShowPreviewParams] = useState(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [severity, setSeverity] = useState("success");
    const [openAlert, setOpenAlert] = useState(false);
    const [alertTitle, setAlertTitle] = useState("");
    const [alertMessage, setAlertMessage] = useState("");
    const [alertAction, setAlertAction] = useState("");

    const VISIBLE_PARAMS = useMemo(() => {
        return [...CONTRACT_COMMON_PARAMS, ...CONTRACT_RESERVATION_PARAMS];
    }, []);

    const {previewContract} = useContractAPI();
    const {data, loading} = useQuery(CONTRACT_TEMPLATES);
    const [upsert] = useMutation(UPSERT_CONTRACT_TEMPLATE, {
        refetchQueries: [{query: CONTRACT_TEMPLATES}],
        awaitRefetchQueries: true,
    });

    const existing = data?.contractTemplates?.[0];

    const [form, setForm] = useState({
        contractType: existing?.contractType || "",
        title: existing?.title || "",
        html: existing?.body || "", // IMPORTANT: use body consistently
        css: existing?.css || DEFAULT_CONTRACT_CSS,
        pageSettings: existing?.pageSettings || {includeHeaderFooter: false},
    });

    const [dirty, setDirty] = useState(false);
    const isValid = useMemo(() => form.contractType && form.html.trim(), [form.contractType, form.html]);
    const isProgrammaticSetRef = useRef(false);

    useEffect(() => {
        if (!existing) return;
        setForm({
            contractType: existing.contractType,
            title: existing.title ?? "",
            html: existing.body ?? "",
            css: existing.css ?? DEFAULT_CONTRACT_CSS,
            pageSettings: existing.pageSettings ?? {includeHeaderFooter: false},
        });
    }, [existing]);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {levels: [1, 2]},
                bulletList: {nested: true},
                orderedList: {nested: true},
                underline: {defaultMark: true},
                fontSizes: [12, 14, 16, 18, 20, 24, 28, 32, 36],
            }),
            Link.configure({openOnClick: false, autolink: true, linkOnPaste: true}),
            Image.configure({inline: false, allowBase64: true}),
            PageBreak,
            Placeholder,
            TextStyle,
            Color,
        ],
        content: "<p></p>",
        onUpdate: ({editor}) => {
            if (isProgrammaticSetRef.current) return;
            setForm((f) => ({...f, html: editor.getHTML()}));
            setDirty(true);
        },
    });

    useEffect(() => {
        if (!editor) return;
        if (!existing) return;

        const rawFromDb = existing.body ?? "<p></p>";
        const migrated = migrateLegacyPlaceholders(rawFromDb);

        isProgrammaticSetRef.current = true;
        editor.commands.setContent(migrated);
        queueMicrotask(() => {
            isProgrammaticSetRef.current = false;
        });

        setForm({
            contractType: existing.contractType,
            title: existing.title ?? "",
            html: migrated,
            css: existing.css ?? DEFAULT_CONTRACT_CSS,
            pageSettings: existing.pageSettings ?? {includeHeaderFooter: false},
        });

        setDirty(false);
    }, [editor, existing]);

    useEffect(() => {
        if (!editor) return;
        editor.commands.setPlaceholderPreview({
            previewValues
        });
    }, [editor, previewValues]);

    const resetAlert = () => {
        setOpenAlert(false);
        setAlertTitle("");
        setAlertMessage("");
        setAlertAction("");
    };

    const handleCancel = () => {
        if (dirty) {
            setAlertTitle(t("confirm"));
            setAlertMessage(t("ignoreChanges"));
            setAlertAction("CANCEL_EDIT");
            setOpenAlert(true);
            return;
        }
        navigate("/beheer/configuratie?tab=contracten");
    };

    const handleCloseAlert = (result, action) => {
        setOpenAlert(false);
        if (result && action === "CANCEL_EDIT") {
            resetAlert();
            navigate("/beheer/configuratie?tab=contracten");
        } else {
            resetAlert();
        }
    };

    useEffect(() => {
        const onBeforeUnload = (e) => {
            if (dirty) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [dirty]);

    const handleSave = async () => {
        if (!isValid) return;

        try {
            // Save as TEMPLATE ({{KEY}}), sanitized for iText safety:
            const bodyForDb = exportToTemplateString(form.html);

            await upsert({
                variables: {
                    input: {
                        contractType: form.contractType,
                        body: bodyForDb,
                    },
                },
            });

            setDirty(false);
            setSnackbarMessage(t("contractTemplateSaved"));
            setSeverity("success");
            setOpenSnackbar(true);
            navigate("/beheer/configuratie?tab=contracten");
        } catch {
            setSnackbarMessage(t("saveFailed"));
            setSeverity("error");
            setOpenSnackbar(true);
        }
    };

    const handlePreviewPdf = async () => {
        if (!existing?.id) {
            setSnackbarMessage(t("saveFirstForPdfPreview"));
            setSeverity("info");
            setOpenSnackbar(true);
            return;
        }

        setIsPreviewLoading(true);
        try {
            const bodyFragment = renderTemplate(form.html, previewValues);
            const blob = await previewContract({
                id: existing.id,
                contractType: form.contractType,
                body: bodyFragment,
            });

            const blobUrl = window.URL.createObjectURL(blob);
            window.open(blobUrl, "_blank");
        } catch (error) {
            console.error("PDF Preview Error:", error);
            setSnackbarMessage(t("pdfPreviewFailed"));
            setSeverity("error");
            setOpenSnackbar(true);
        } finally {
            setIsPreviewLoading(false);
        }
    };

    if (loading) return <Loading/>;

    return (
        <Box sx={{width: "100%", paddingLeft: "100px"}}>
            {/* Client contract header */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    bgcolor: "background.paper",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Button onClick={handleCancel} data-testid="back-button">{t("back")}</Button>
                <Typography variant="h5">{t("editClientContract")}</Typography>
                <Box sx={{display: "flex", gap: 1}}>
                    <Button
                        variant="outlined"
                        onClick={handlePreviewPdf}
                        disabled={!existing?.id || isPreviewLoading}
                        startIcon={isPreviewLoading ? <CircularProgress size={20}/> : null}
                        data-testid="pdf-preview-button"
                    >
                        {t("pdfPreview")}
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={!isValid} data-testid="save-contract-button">
                        {t("save")}
                    </Button>
                </Box>
            </Box>


            {/* RichTextEditor */}
            <Box sx={{paddingTop: 1}}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                        <Paper variant="outlined">
                            {editor && (
                                <Box
                                    sx={{
                                        height: "70vh",
                                        overflow: "auto",
                                        position: "relative",
                                    }}
                                >
                                    <RichTextEditorProvider editor={editor}>
                                        <RichTextField
                                            variant="outlined"
                                            controls={
                                                <MenuControlsContainer>
                                                    <MenuButtonUndo/>
                                                    <MenuButtonRedo/>
                                                    <MenuDivider/>

                                                    <MenuSelectHeading/>
                                                    <MenuDivider/>

                                                    <MenuButtonBold/>
                                                    <MenuButtonItalic/>
                                                    <MenuButtonUnderline/>
                                                    <MenuButtonTextColor/>
                                                    <MenuDivider/>

                                                    <MenuButtonBulletedList/>
                                                    <MenuButtonOrderedList/>
                                                    <MenuDivider/>

                                                    <MenuDivider/>
                                                    {t('atCursor')}
                                                    <MenuButtonPageBreak editor={editor} t={t}/>
                                                    <MenuButtonInsertPlaceholder editor={editor} t={t}/>
                                                    <MenuDivider/>
                                                </MenuControlsContainer>
                                            }
                                            sx={editorPdfLikeSx(t)}
                                        />
                                    </RichTextEditorProvider>
                                </Box>
                            )}

                            <Divider sx={{my: 2}}/>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    cursor: "pointer",
                                    mb: 1,
                                }}
                                onClick={() => setShowPreviewParams((v) => !v)}
                            >
                                <Typography variant="subtitle2">{t("previewValues")}</Typography>

                                <Button size="small">{showPreviewParams ? t("hide") : t("show")}</Button>
                            </Box>

                            <Collapse in={showPreviewParams} timeout="auto" unmountOnExit>
                                <Grid container spacing={1} sx={{mb: 2}}>
                                    {VISIBLE_PARAMS.map((p) => (
                                        <Grid item xs={12} sm={6} key={p}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                label={p}
                                                value={previewValues[p] ?? ""}
                                                onChange={(e) => setPreviewValues((v) => ({...v, [p]: e.target.value}))}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Collapse>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            <AppSnackbar
                openSnackbar={openSnackbar}
                snackbarMessage={snackbarMessage}
                setOpenSnackbar={setOpenSnackbar}
                severity={severity}
            />

            <AlertDialogSlide
                title={alertTitle}
                message={alertMessage}
                openAlert={openAlert}
                action={alertAction}
                onClose={handleCloseAlert}
            />
        </Box>
    );
};

export default withAuthenticationRequired(ContractTemplateEditor, {
    onRedirecting: () => <Loading/>,
    returnTo: "/beheer/login",
});
